import { Params } from '@sentry/react/types/types';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../../../actions/framework';
import { COLUMN_ACTION_GROUP } from '../../constants';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { TaxonomyColumnModel } from '../taxonomy-column.model';
import { toast } from '@cotera/client/app/components/ui';
import { EraQL } from '@cotera/era';
import { Assert } from '@cotera/utilities';

export class RunPreviewTaxonomyColumnAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Run Column Preview';
  inputSchema = z.object({});
  shortcut = ['ctrl', 'enter'];
  group = COLUMN_ACTION_GROUP;
  icon = 'play';

  applicable(context: ApplicableContext): boolean {
    return (
      context.hasTargetedModelOfType(TaxonomyColumnModel) &&
      !!context
        .targetedModelOfType(TaxonomyColumnModel)
        ?.datasetRel.snapshot()
        ?.isOk()
    );
  }

  async execute(
    _: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(TaxonomyColumnModel);
    Assert.assert(model !== null);

    const taxonomyColName = model.currentNameInGraph.snapshot();

    toast.info(`Running preview for ${taxonomyColName}.`);

    const datasetRel_res = model.datasetRel.snapshot();
    if (datasetRel_res === null || datasetRel_res.isErr()) {
      return err([
        {
          t: 'invalid-dataset',
          message: JSON.stringify(datasetRel_res?.error),
        },
      ]);
    }

    const datasetRel = datasetRel_res.value.rel;

    const eraQL = model.eraQL.snapshot() || '';

    const taxonomyExpr_res = EraQL.parseExpr(eraQL, {
      attributes: datasetRel.attributes,
    });
    if (taxonomyExpr_res.isErr()) {
      return err([
        {
          t: 'taxonomy-compilation-error',
          message: JSON.stringify(taxonomyExpr_res.error),
        },
      ]);
    }

    const taxonomyExpr = taxonomyExpr_res.value;
    const taxonomyExprDeps = Object.keys(taxonomyExpr.attrReqs.from ?? {});

    const preview = datasetRel.select(
      (t) => ({
        ...Object.fromEntries(
          taxonomyExprDeps.map((attr) => [attr, t.attr(attr)])
        ),
        [taxonomyColName]: taxonomyExpr,
      }),
      {
        distinct: true,
        limit: 300,
      }
    );

    model.preview.set(preview);

    return ok({});
  }
}
